/**
 * Game Level Generation System
 * Generates 15 levels with increasing difficulty
 */

// Sample texts for different levels
const SAMPLE_TEXTS = [
  'HELLO',
  'SECRET MESSAGE',
  'CRYPTOGRAPHY IS FUN',
  'THE QUICK BROWN FOX JUMPS OVER THE LAZY DOG',
  'INFORMATION SECURITY IS CRITICAL IN MODERN COMPUTING',
  'CLASSICAL CIPHERS WERE USED FOR CENTURIES BEFORE MODERN CRYPTOGRAPHY',
  'PLAYFAIR CIPHER USES A FIVE BY FIVE MATRIX TO ENCODE MESSAGES',
  'HILL CIPHER IS A POLYGRAPHIC SUBSTITUTION CIPHER BASED ON LINEAR ALGEBRA',
  'VIGENERE CIPHER IS A METHOD OF ENCRYPTING TEXT BY USING A SERIES OF INTERWOVEN CAESAR CIPHERS',
  'TRANSPOSITION CIPHERS REARRANGE THE POSITIONS OF CHARACTERS RATHER THAN SUBSTITUTING THEM',
  'MONOALPHABETIC SUBSTITUTION USES A FIXED MAPPING BETWEEN PLAINTEXT AND CIPHERTEXT CHARACTERS',
  'POLYALPHABETIC CIPHERS USE MULTIPLE SUBSTITUTION ALPHABETS TO INCREASE SECURITY',
  'THE ENIGMA MACHINE WAS A SOPHISTICATED CIPHER DEVICE USED DURING WORLD WAR TWO',
  'MODERN CRYPTOGRAPHY RELIES ON COMPUTATIONAL COMPLEXITY AND MATHEMATICAL PROBLEMS',
  'QUANTUM COMPUTING MAY ONE DAY BREAK MANY OF TODAYS CRYPTOGRAPHIC SYSTEMS'
];

// Cipher distribution across levels
const CIPHER_DISTRIBUTION = [
  'caesar',           // Level 1
  'caesar',           // Level 2
  'polybius',         // Level 3
  'railfence',        // Level 4
  'transposition',    // Level 5
  'monoalphabetic',   // Level 6
  'vigenere',         // Level 7
  'playfair',         // Level 8
  'rowColumnTransposition', // Level 9
  'hill',             // Level 10
  'monoalphabetic',   // Level 11
  'vigenere',         // Level 12
  'playfair',         // Level 13
  'hill',             // Level 14
  'rowColumnTransposition'  // Level 15
];

// Generate keys for each cipher type
function generateKey(cipherType, level) {
  switch (cipherType) {
    case 'caesar':
      return String(3 + (level % 23)); // Shift between 3-25
    case 'railfence':
      return String(2 + Math.min(level, 5)); // 2-7 rails
    case 'transposition':
    case 'rowColumnTransposition':
      const keywords = ['KEY', 'SECRET', 'CIPHER', 'CRYPTO', 'PASSWORD', 'ENCRYPT'];
      return keywords[Math.min(level - 1, keywords.length - 1)];
    case 'monoalphabetic':
      // Generate a random 26-letter permutation
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const shuffled = alphabet.split('').sort(() => Math.random() - 0.5);
      return shuffled.join('');
    case 'vigenere':
      const vigenereKeys = ['KEY', 'SECRET', 'CIPHER', 'CRYPTO', 'PASSWORD'];
      return vigenereKeys[Math.min(Math.floor(level / 2), vigenereKeys.length - 1)];
    case 'playfair':
      const playfairKeys = ['KEYWORD', 'SECRET', 'CIPHER', 'CRYPTO', 'PASSWORD'];
      return playfairKeys[Math.min(Math.floor(level / 2), playfairKeys.length - 1)];
    case 'hill':
      // Use 2x2 for levels 10-12, 3x3 for 13-15
      if (level <= 12) {
        // 2x2 matrix: must be invertible mod 26
        return '2x2:3,3,2,5'; // Determinant = 9, invertible
      } else {
        // 3x3 matrix
        return '3x3:6,24,1,13,16,10,20,17,15'; // Determinant = 1, invertible
      }
    default:
      return 'KEY';
  }
}

/**
 * Picks cipher for a given level
 */
function pickCipherForLevel(level) {
  return CIPHER_DISTRIBUTION[level - 1] || 'caesar';
}

/**
 * Computes difficulty based on level and cipher
 */
function computeDifficulty(level, cipher) {
  const textIndex = Math.min(level - 1, SAMPLE_TEXTS.length - 1);
  return {
    textLength: SAMPLE_TEXTS[textIndex].length,
    keyComplexity: level,
    cipherComplexity: ['caesar', 'polybius', 'railfence'].includes(cipher) ? 1 :
                     ['transposition', 'monoalphabetic', 'vigenere'].includes(cipher) ? 2 : 3
  };
}

/**
 * Generates hints for a level
 */
function generateHints(level, cipher, mode) {
  const hints = [];
  
  if (level <= 3) {
    hints.push(`This is a ${cipher} cipher`);
    hints.push(`Try the basic ${cipher} rules`);
  } else if (level <= 6) {
    hints.push(`Cipher type: ${cipher}`);
    hints.push(`Check the key format`);
  } else {
    hints.push(`Cipher: ${cipher}`);
    hints.push(`Key structure matters`);
    hints.push(`Review ${cipher} algorithm`);
  }
  
  return hints;
}

/**
 * Generates a level configuration
 * @param {number} level - Level number (1-15)
 * @returns {object} Level configuration
 */
export function generateLevel(level) {
  if (level < 1 || level > 15) {
    throw new Error('Level must be between 1 and 15');
  }

  const cipher = pickCipherForLevel(level);
  const difficulty = computeDifficulty(level, cipher);
  const key = generateKey(cipher, level);
  const plaintext = SAMPLE_TEXTS[Math.min(level - 1, SAMPLE_TEXTS.length - 1)];
  
  // Determine mode: alternate or based on level
  const mode = level % 2 === 1 ? 'encrypt' : 'decrypt';
  
  // Calculate guesses: 10, 8, 6, 4, then 2, clamping to minimum 1
  const baseGuesses = Math.max(1, 10 - 2 * (level - 1));
  const guessesAllowed = Math.max(1, baseGuesses);
  
  let challengeText = '';
  let answerText = '';
  
  if (mode === 'encrypt') {
    challengeText = plaintext;
    answerText = ''; // Will be computed by backend
  } else {
    // For decrypt mode, we need to encrypt first to get ciphertext
    // This will be handled by the game component calling the backend
    challengeText = ''; // Will be computed
    answerText = plaintext;
  }

  return {
    level,
    cipher,
    mode,
    plaintext,
    key,
    challengeText, // Will be set by game component
    answerText,
    guessesAllowed,
    guessesLeft: guessesAllowed,
    hints: generateHints(level, cipher, mode),
    hintsUsed: 0,
    timeLimitSec: level > 10 ? 300 : null, // 5 min limit for high levels
    difficulty,
    completed: false,
    score: 0,
    startTime: null,
    endTime: null
  };
}

/**
 * Get all level configurations
 */
export function getAllLevels() {
  return Array.from({ length: 15 }, (_, i) => generateLevel(i + 1));
}

/**
 * Get cipher display name
 */
export function getCipherDisplayName(cipher) {
  const names = {
    caesar: 'Caesar Cipher',
    polybius: 'Polybius Cipher',
    railfence: 'Rail Fence Cipher',
    transposition: 'Transposition Cipher',
    monoalphabetic: 'Monoalphabetic Cipher',
    vigenere: 'Vigenère Cipher',
    playfair: 'Playfair Cipher',
    hill: 'Hill Cipher',
    rowColumnTransposition: 'Row/Column Transposition'
  };
  return names[cipher] || cipher;
}

/**
 * Get cipher icon
 */
export function getCipherIcon(cipher) {
  const icons = {
    caesar: '🔐',
    polybius: '📊',
    railfence: '🚂',
    transposition: '🔄',
    monoalphabetic: '🔑',
    vigenere: '🌀',
    playfair: '🎯',
    hill: '⛰️',
    rowColumnTransposition: '📋'
  };
  return icons[cipher] || '🔒';
}

